/**
 * HTML character references, decoded — for text a provider hands over still
 * escaped, where a reader would otherwise see "Alzheimer&apos;s".
 *
 * A search result's title arrives the way the page's `<title>` was written, and
 * some pages write theirs with references in it. The providers pass it through
 * untouched, so the thread printed the escape itself. A usability study caught
 * it on a source list; nothing about the page was wrong, only its spelling.
 *
 * Deliberately not a parser, and deliberately no DOM: `core/` has none, and a
 * `textarea.innerHTML` round trip is exactly the kind of HTML-string handling
 * the thread's renderer refuses. A small closed table of the references titles
 * actually carry, plus both numeric forms, in ONE pass — so `&amp;lt;` becomes
 * the literal `&lt;` its author wrote rather than a `<` nobody did. Anything
 * not recognised is left exactly as it arrived: an unknown name or an
 * impossible code point is somebody's text, not a reference this can read.
 *
 * WHERE it is applied matters more than what it does. A source's TITLE is
 * decoded when the search result is collected (`engine/providers/*`), because
 * no gate reads a title — the citation gate matches URLs, and the quote check
 * and the artefact signatures read page TEXT. Page text and quotes are NOT
 * decoded on the way in: `quoteFound`, `textHash` and `simHash` are computed
 * over them, and decoding one side of a comparison and not the other would
 * change what the gate decides. A quote is decoded where it is SHOWN, and only
 * there.
 */

const NAMED: Readonly<Record<string, string>> = {
  amp: '&',
  lt: '<',
  gt: '>',
  quot: '"',
  apos: "'",
  nbsp: '\u00a0',
  // The typographic ones a headline writer reaches for, which is most of what a
  // title carries besides the five above.
  ndash: '–',
  mdash: '—',
  hellip: '…',
  lsquo: '‘',
  rsquo: '’',
  ldquo: '“',
  rdquo: '”',
}

const REFERENCE = /&(?:#(\d{1,7})|#[xX]([0-9a-fA-F]{1,6})|([a-zA-Z]+));/g

/** A code point a reference may name: in range, not a surrogate half, not NUL. */
const speakable = (cp: number): boolean =>
  cp > 0 && cp <= 0x10ffff && !(cp >= 0xd800 && cp <= 0xdfff)

/** Decode the references this knows, once each, and leave everything else. */
export function decodeEntities(text: string): string {
  if (!text.includes('&')) return text
  return text.replace(REFERENCE, (whole, dec?: string, hex?: string, name?: string) => {
    if (name !== undefined) return NAMED[name] ?? whole
    const cp = dec !== undefined ? Number.parseInt(dec, 10) : Number.parseInt(hex!, 16)
    return speakable(cp) ? String.fromCodePoint(cp) : whole
  })
}

/**
 * A title as a reader should see it: decoded, on one line, trimmed. The
 * whitespace fold is because a decoded `&nbsp;` or `&#10;` is still a gap, and a
 * title is one line wherever it is printed.
 */
export function cleanTitle(text: string): string {
  return decodeEntities(text).replace(/\s+/g, ' ').trim()
}
